"use client";

/* eslint-disable @next/next/no-img-element */
import { PopupTitle } from "@commonninja/nindo-ui";
import cn from "classnames";
import { useSearchParams } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { ITemplatesPageData, IPlugin, ITemplate } from "../../types";
import { PrimaryButton } from "../button/button.styles";
import { CustomLink } from "../link/link.comp";
import { trackEvent } from "../../lib/track";
import {
  StyledPopup,
  StyledWidgetExamplesSection,
  StyledSectionTags,
} from "./WidgetExamplesSection.styles";

// Template gallery engagement tracking (additive). Preview = interest, Use = the real success
// signal (visitor clones the template into their account). Fires the site's standard Mixpanel
// event AND mirrors to our own Mongo (/api/templates/track) so reports are pullable directly.
const reportTemplateEvent = (
  event: "template_preview" | "template_use",
  source: "card" | "preview",
  template: ITemplate
) => {
  const props = {
    componentType: template.componentType,
    templateName: template.title || template.name || "",
    templateSlug: template.slug || "",
    templateId: template._id || "",
    widgetId: template.widgetId || "",
    source,
  };
  trackEvent(
    event === "template_preview" ? "Template - Preview" : "Template - Use",
    props
  );
  try {
    // Fire-and-forget; keepalive so it survives the navigation the "Use" link triggers.
    fetch("/api/templates/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, ...props }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* never let tracking break the click */
  }
};

interface WidgetExamplesSectionProps {
  templatesData: ITemplatesPageData;
  title?: string;
  hideNames?: boolean;
  allWidgets?: IPlugin[];
  tagsMode?: 'group' | 'filter';
}

export const WidgetExamplesSection = ({
  templatesData,
  title,
  hideNames = false,
  allWidgets,
  tagsMode = 'group',
}: WidgetExamplesSectionProps) => {
  const { templates, tags } = templatesData;
  const searchParams = useSearchParams();
  const [activeWidgetPreview, setActiveWidgetPreview] =
    useState<ITemplate | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [selectedFilterTags, setSelectedFilterTags] = useState<Set<string>>(new Set());

  // Create refs for each holiday section
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const hasInitializedFromUrl = useRef(false);

  // Initialize tags from URL query params (supports ?tags=tag1,tag2,tag3)
  useEffect(() => {
    if (hasInitializedFromUrl.current) return;
    
    const tagsParam = searchParams.get('tags');
    if (!tagsParam) return;
    
    const tagIds = tagsParam.split(',').map(id => id.trim()).filter(Boolean);
    if (tagIds.length === 0) return;
    
    hasInitializedFromUrl.current = true;
    
    if (tagsMode === 'filter') {
      setSelectedFilterTags(new Set(tagIds));
    } else {
      // In group mode, scroll to the first tag section after DOM is ready
      setTimeout(() => {
        const firstTagId = tagIds[0];
        const element = sectionRefs.current[firstTagId];
        if (element) {
          const offset = 120;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
          setActiveTag(firstTagId);
        }
      }, 100);
    }
  }, [searchParams, tagsMode]);

  // Toggle a filter tag selection
  const toggleFilterTag = (tagId: string) => {
    setSelectedFilterTags((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  // Get filtered templates for 'filter' mode (AND logic - must have ALL selected tags)
  const filteredTemplates = 
    selectedFilterTags.size === 0
      ? templates
      : templates.filter((template) =>
          Array.from(selectedFilterTags).every((filterTag) => template.tags?.includes(filterTag))
        );

  // Group templates by tags
  const templatesByTag =
    tags.length > 0
      ? tags
          .map((tag) => {
            const tagTemplates = templates.filter((template) =>
              template.tags?.includes(tag._id || tag.title)
            );
            return {
              tag,
              templates: tagTemplates,
            };
          })
          .filter((group) => group.templates.length > 0)
      : [
          {
            tag: { title: title || "", _id: "all-templates" },
            templates: templates,
          },
        ]; // Only show tags that have templates

  // Scroll to tag section
  const scrollToSection = (tagId: string) => {
    const element = sectionRefs.current[tagId];
    if (element) {
      const offset = 120; // Account for fixed header + tags bar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveTag(tagId);
    }
  };

  function renderWidgetPreview(template: ITemplate | null) {
    if (!template) return null;
    return (
      <div key={`template-${template._id}`} className={`template-preview`}>
        <div className="widget-display">
          <iframe
            src={`https://widgets.commoninja.com/iframe/${
              template.previewWidgetId || template.widgetId
            }?visible=true`}
            width="100%"
            height="100%"
            frameBorder="0"
            scrolling="no"
          ></iframe>
        </div>
      </div>
    );
  }

  function renderTemplate(template: ITemplate) {
    const widget = allWidgets?.find(
      (widget) => widget.componentType === template.componentType
    );
    const link = `https://www.commoninja.com/${
      widget?.slug || template.componentType.replaceAll("_", "-")
    }/editor/content?template=${template.slug || template._id}`;

    return (
      <div>
        <div key={template.widgetId} className="template">
          <img
            className={cn("template-thumbnail", template.type)}
            src={template.thumbnail}
            alt={template.name}
          />

          <div className="template-actions">
            <PrimaryButton
              onClick={() => {
                setActiveWidgetPreview(template);
                reportTemplateEvent("template_preview", "card", template);
              }}
            >
              Preview
            </PrimaryButton>
            <CustomLink
              target="_blank"
              href={link}
              variant="primary"
              onClick={() => reportTemplateEvent("template_use", "card", template)}
            >
              Use
            </CustomLink>
          </div>
        </div>
        {!hideNames && (
          <div className="template-details">
            {template.title && <h3>{template.title}</h3>}
            {/* {template.description && <p>{template.description}</p>} */}
            {template.tags && template.tags.length > 0 && (
              <div className="template-tags">
                {template.tags.map((tagId) => {
                  const tagObj = tags.find((t) => t._id === tagId || t.title === tagId);
                  return (
                    <span key={tagId} className="template-tag">
                      {tagObj?.title || tagId}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderPopup() {
    if (!activeWidgetPreview) return null;
    const widget = allWidgets?.find(
      (widget) => widget.componentType === activeWidgetPreview.componentType
    );
    const slug =
      widget?.slug || activeWidgetPreview.componentType.replaceAll("_", "-");
    return (
      <StyledPopup
        show={activeWidgetPreview !== null}
        onClose={() => setActiveWidgetPreview(null)}
        style={{
          maxWidth: "95%",
          width: "1100px",
          color: "black",
          zIndex: 99999,
        }}
        closeButtonStyle={{
          fontSize: 16,
          width: 20,
          height: 20,
          top: -7.5,
          right: -7.5,
          padding: 0,
          margin: 0,
          backgroundColor: "white",
          borderRadius: 50,
          boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.25)",
        }}
      >
        <PopupTitle>
          <div className="preview-description">
            {activeWidgetPreview?.title && (
              <h3 className="preview-title">
                {activeWidgetPreview?.title || ""}
              </h3>
            )}
            {activeWidgetPreview?.description && (
              <p className="preview-description">
                {activeWidgetPreview?.description || ""}
              </p>
            )}
          </div>
          <PrimaryButton>
            <CustomLink
              target="_blank"
              href={`https://www.commoninja.com/${slug}/editor/content?template=${
                activeWidgetPreview?.slug || activeWidgetPreview?._id
              }`}
              variant="primary"
              onClick={() =>
                activeWidgetPreview &&
                reportTemplateEvent("template_use", "preview", activeWidgetPreview)
              }
            >
              Use Example
            </CustomLink>
          </PrimaryButton>
        </PopupTitle>
        {renderWidgetPreview(activeWidgetPreview)}
      </StyledPopup>
    );
  }

  return (
    <>
      {tags.length > 0 && (
        <StyledSectionTags className="section-tags">
          <div className="tags-container">
            {tagsMode === 'filter' && (
              <button
                className={`tag ${selectedFilterTags.size === 0 ? "active" : ""}`}
                onClick={() => setSelectedFilterTags(new Set())}
              >
                All
              </button>
            )}
            {tags.map((tag) => {
              const tagId = tag._id || tag.title;
              if (tagsMode === 'filter') {
                return (
                  <button
                    key={tagId}
                    className={`tag ${selectedFilterTags.has(tagId) ? "active" : ""}`}
                    onClick={() => toggleFilterTag(tagId)}
                  >
                    {tag.title}
                  </button>
                );
              }
              return (
                <button
                  key={tagId}
                  className={`tag ${activeTag === tagId ? "active" : ""}`}
                  onClick={() => scrollToSection(tagId)}
                >
                  {tag.title}
                </button>
              );
            })}
          </div>
        </StyledSectionTags>
      )}

      <StyledWidgetExamplesSection className="templates-section">
        {tagsMode === 'filter' ? (
          <div className="tag-section">
            {title && <h2 className="tag-title">{title}</h2>}
            <div className="templates-content-wrapper">
              {filteredTemplates.map((template) => renderTemplate(template))}
            </div>
          </div>
        ) : (
          templatesByTag.map(({ tag, templates }) => {
            const tagId = tag._id || tag.title;
            return (
              <div
                key={tagId}
                className="tag-section"
                ref={(el) => {
                  sectionRefs.current[tagId] = el;
                }}
              >
                {tag.title && (
                  <h2 className="tag-title">{tag.title}</h2>
                )}
                <div className="templates-content-wrapper">
                  {templates.map((template) => renderTemplate(template))}
                </div>
              </div>
            );
          })
        )}
      </StyledWidgetExamplesSection>
      {renderPopup()}
    </>
  );
};
